<template>
  <Fenetre :titre="session ? 'Ma caisse' : 'Ouvrir ma caisse'" taille="large" @fermer="$emit('fermer')">
    <!-- Ouverture -->
    <div v-if="!session">
      <p class="text-muted">Comptez les espèces présentes dans le tiroir avant de commencer à vendre.</p>
      <div class="grille-form">
        <div>
          <label class="form-label">Boutique</label>
          <select v-model="siteId" class="form-select">
            <option v-for="s in boutiques" :key="s.id" :value="s.id">{{ s.nom }}</option>
          </select>
        </div>
        <div>
          <label class="form-label">Fond de caisse (F)</label>
          <input v-model.number="fond" type="number" min="0" class="form-control chiffre" />
        </div>
      </div>
    </div>

    <!-- Session ouverte -->
    <div v-else>
      <div class="indicateurs">
        <div class="indicateur"><div class="libelle">Ouverte le</div><div class="valeur" style="font-size:1.1rem">{{ date(session.ouvertureAt, true) }}</div></div>
        <div class="indicateur"><div class="libelle">Ventes</div><div class="valeur">{{ session.nombreVentes }}</div></div>
        <div class="indicateur"><div class="libelle">Chiffre d'affaires</div><div class="valeur">{{ fcfa(session.chiffreAffaires) }}</div></div>
        <div class="indicateur principal"><div class="libelle">Espèces attendues</div><div class="valeur">{{ fcfa(session.especesAttendues) }}</div></div>
      </div>
      <table class="table table-sm table-boutique mb-3">
        <thead><tr><th>Moyen</th><th class="num">Montant</th></tr></thead>
        <tbody>
          <tr><td>Fond de caisse</td><td class="num">{{ fcfa(session.fondInitial) }}</td></tr>
          <tr v-for="(m, nom) in session.parMoyen" :key="nom"><td>{{ nom }}</td><td class="num">{{ fcfa(m) }}</td></tr>
          <tr v-if="session.depensesEspeces"><td>Dépenses payées en espèces</td><td class="num text-danger">− {{ fcfa(session.depensesEspeces) }}</td></tr>
        </tbody>
      </table>

      <h4 class="h6">Clôturer : comptage des espèces</h4>
      <div class="billets">
        <label v-for="b in coupures" :key="b" class="billet">
          <span>{{ fcfa(b, false) }}</span>
          <input v-model.number="comptage[b]" type="number" min="0" class="form-control form-control-sm" :aria-label="'Nombre de ' + b" />
        </label>
      </div>
      <div class="d-flex align-items-center gap-3 mt-2 flex-wrap">
        <div>Total compté : <strong class="chiffre fs-5">{{ fcfa(totalCompte) }}</strong></div>
        <div v-if="ecart !== 0" :class="ecart < 0 ? 'text-danger' : 'text-success'">Écart {{ ecart > 0 ? '+' : '' }}{{ fcfa(ecart) }}</div>
        <div v-else class="text-success"><i class="bi bi-check-circle"></i> La caisse est juste</div>
      </div>
      <textarea v-if="ecart !== 0" v-model="note" class="form-control mt-2" rows="2" placeholder="Expliquez l'écart (obligatoire)"></textarea>
      <div v-if="ventesHorsLigne" class="alert alert-warning mt-3 mb-0 py-2">
        <i class="bi bi-wifi-off"></i> {{ ventesHorsLigne }} vente(s) faite(s) hors ligne ne sont pas encore envoyées : la caisse ne peut être clôturée qu'après leur envoi, sinon leurs espèces manqueraient au comptage.
      </div>
    </div>

    <template #pied>
      <button class="btn btn-light" @click="$emit('fermer')">Fermer</button>
      <button v-if="!session" class="btn btn-safran" :disabled="enCours || !siteId" @click="ouvrir">Ouvrir la caisse</button>
      <button v-else class="btn btn-primary" :disabled="enCours || ventesHorsLigne > 0 || (ecart !== 0 && !note.trim())" @click="cloturer">Clôturer la caisse</button>
    </template>
  </Fenetre>
</template>

<script>
import api, { messageErreur, notifier } from "@/api";
import Fenetre from "@/components/commun/Fenetre.vue";
import { fcfa, date } from "@/utils/format";
import { mesVentesEnAttente } from "@/horsLigne";

export default {
  name: "MaCaisse",
  components: { Fenetre },
  emits: ["fermer"],
  data() {
    return {
      fond: 0, siteId: this.$store.state.siteId, note: "", enCours: false,
      coupures: [10000, 5000, 2000, 1000, 500, 250, 200, 100, 50, 25, 10, 5],
      comptage: {},
    };
  },
  computed: {
    ventesHorsLigne() { return mesVentesEnAttente().length; },
    session() { return this.$store.state.caisse; },
    boutiques() { return this.$store.state.sites.filter(s => s.type === "BOUTIQUE"); },
    totalCompte() { return this.coupures.reduce((t, b) => t + b * (Number(this.comptage[b]) || 0), 0); },
    ecart() { return this.session ? this.totalCompte - this.session.especesAttendues : 0; },
  },
  async mounted() {
    await this.$store.dispatch("chargerCaisse");
    if (!this.boutiques.find(b => b.id === this.siteId)) this.siteId = this.boutiques[0]?.id;
  },
  methods: {
    fcfa, date,
    async ouvrir() {
      this.enCours = true;
      try {
        const { data } = await api.post("caisse/ouvrir", { siteId: this.siteId, fondInitial: this.fond || 0 });
        this.$store.commit("setCaisse", data.session);
        this.$store.commit("setSite", this.siteId);
        notifier("Caisse ouverte. Bonnes ventes !", "success");
        this.$emit("fermer");
      } catch (e) {
        notifier(messageErreur(e));
      } finally {
        this.enCours = false;
      }
    },
    async cloturer() {
      this.enCours = true;
      try {
        await api.post("caisse/fermer", { especesComptees: this.totalCompte, note: this.note });
        this.$store.commit("setCaisse", null);
        notifier("Caisse clôturée.", "success");
        this.$emit("fermer");
      } catch (e) {
        notifier(messageErreur(e));
      } finally {
        this.enCours = false;
      }
    }
  }
};
</script>

<style scoped>
.billets { display: grid; grid-template-columns: repeat(auto-fill, minmax(110px, 1fr)); gap: 8px; }
.billet { display: flex; align-items: center; gap: 6px; background: var(--fond); padding: 6px 8px; border-radius: 6px; }
.billet span { font-family: var(--police-chiffres); font-weight: 700; min-width: 48px; }
</style>
